import { Injectable } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import {BehaviorSubject, Observable} from "rxjs";
import { environment } from '../../../environments/environment';
import { tap } from 'rxjs/operators';
import { JwtUser } from '../../models/user';
import { JwtResponse } from '../../../auth/models/jwt.response';

export interface LoginResponse {
  session: string;
}

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private loggedIn = new BehaviorSubject<boolean>(false);
  isLoggedIn$ = this.loggedIn.asObservable();

  constructor(private http: HttpClient) { }

  login(email: string): Observable<LoginResponse> {
    const requestBody = { email: email };
    return this.http.post<LoginResponse>(
      `${environment.api}/auth/request-otp`,
      requestBody,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
  }

  verifyOtp(email: string, otp: string, session: string): Observable<JwtResponse> {
    const requestBody = {
      email: email,
      otp: otp,
      session: session
    };

    return this.http.post<JwtResponse>(
      `${environment.api}/auth/login`,
      requestBody,
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': '*/*'
        }
      }
    );
  }

  setUser(idToken: string) {
    localStorage.setItem('id_token', idToken);
  }

  getUser(): JwtUser | null {
    const idToken = localStorage.getItem('id_token') ?? "";
    const user = this.decodeToken(idToken);
    return user ? user : null;
  }

  setToken(token : string) {
    localStorage.setItem('access_token', token);
  }

  getToken() : string {
    return localStorage.getItem('access_token') ?? '';
  }

  removeToken() {
    localStorage.removeItem('access_token');
  }

  canActivate() : boolean {
    const token = this.getToken();
    if(!token || token === '') return false;
    if(this.isTokenValid(token)) {
      return true;
    } else {
        this.removeToken();
        return false;
    }
  }

  decodeToken(token : string) : any {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    // Pad base64 string if necessary
    const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, "=");
    // Decode base64 to UTF-8
    const jsonPayload = decodeURIComponent(
      atob(padded)
        .split("")
        .map(c => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  }

  isTokenExpired(token : string) : boolean {
    const decodedToken = this.decodeToken(token);
    const now = Date.now().valueOf() / 1000;
    return decodedToken.exp < now;
  }

  isTokenValid(token: string) {
    if(!token || token === '') return false;
    return !this.isTokenExpired(token);
  }

  setLoggedIn(value : boolean) {
    this.loggedIn.next(value);
  }

  removeUser() {
    localStorage.removeItem('id_token');
  }

  logout() {
    this.removeToken();
    this.removeUser();
    this.setLoggedIn(false);
  }
}
